import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Shield, Smartphone, CheckCircle, AlertCircle, Lock, QrCode, Key } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/config';


const TwoFactorSettings = () => {
  const [status, setStatus] = useState({ enabled: false, required: false });
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  
  // Setup flow
  const [setupStep, setSetupStep] = useState(1);
  const [password, setPassword] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  
  // Disable flow
  const [disablePassword, setDisablePassword] = useState('');
  const [disableOtp, setDisableOtp] = useState('');
  
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
      setLoading(false);
    }
  };

  const handleStartSetup = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/api/auth/2fa/setup`,
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
      setManualKey(response.data.manual_entry_key);
      setSetupStep(2);
      setPassword('');
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Errore durante la configurazione'
      });
    }
  };

  const handleEnableSep = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(
        `${API}/api/auth/2fa/enable`,
        { secret, otp_code: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage({ type: 'success', text: '2FA attivato con successo!' });
      setShowSetup(false);
      setSetupStep(1);
      setVerificationCode('');
      fetchStatus();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Codice non valido. Riprova.'
      });
    }
  };

  const handleDisable2FA = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(
        `${API}/api/auth/2fa/disable`,
        { password: disablePassword, otp_code: disableOtp },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage({ type: 'success', text: '2FA disattivato con successo' });
      setShowDisable(false);
      setDisablePassword('');
      setDisableOtp('');
      fetchStatus();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Errore durante la disattivazione'
      });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p>Caricamento...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Shield className="h-7 w-7 text-blue-600" />
            Autenticazione a Due Fattori (2FA)
          </h2>
          <p className="text-slate-600 mt-2">
            Proteggi il tuo account con un livello aggiuntivo di sicurezza
          </p>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg border ${
            message.type === 'success' 
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5 inline mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 inline mr-2" />
            )}
            {message.text}
          </div>
        )}

        {/* Current Status */}
        <Card className="p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-3 rounded-full ${
                  status.enabled ? 'bg-green-100' : 'bg-slate-100'
                }`}>
                  <Shield className={`h-6 w-6 ${
                    status.enabled ? 'text-green-600' : 'text-slate-400'
                  }`} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">
                    Stato: {status.enabled ? 'Attivo' : 'Non Attivo'}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {status.enabled 
                      ? 'Il tuo account è protetto con 2FA' 
                      : 'Il tuo account non è protetto con 2FA'}
                  </p>
                </div>
              </div>

              {status.required && !status.enabled && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">2FA Consigliato</p>
                      <p>Per il tuo ruolo, si consiglia fortemente di attivare l'autenticazione a due fattori.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              {!status.enabled ? (
                <Button
                  onClick={() => setShowSetup(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Attiva 2FA
                </Button>
              ) : (
                <Button
                  onClick={() => setShowDisable(true)}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  Disattiva 2FA
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-slate-50">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-blue-600" />
            Come Funziona la 2FA
          </h3>
          <div className="space-y-3 text-sm text-slate-700">
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                1
              </span>
              <p>Scarica un'app di autenticazione (Google Authenticator, Microsoft Authenticator, Authy)</p>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                2
              </span>
              <p>Scansiona il QR code con l'app o inserisci manualmente la chiave</p>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                3
              </span>
              <p>Al login, dopo email e password, inserisci il codice di 6 cifre dall'app</p>
            </div>
          </div>
        </Card>

        {/* Setup Dialog */}
        <Dialog open={showSetup} onOpenChange={setShowSetup}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-blue-600" />
                Attiva Autenticazione a Due Fattori
              </DialogTitle>
            </DialogHeader>

            {setupStep === 1 && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Per attivare la 2FA, conferma la tua password.
                </p>
                <div>
                  <Label htmlFor="setup-password">Password</Label>
                  <Input
                    id="setup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Inserisci la tua password"
                    className="mt-1"
                  />
                </div>
                <Button
                  onClick={handleStartSetup}
                  disabled={!password}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Continua
                </Button>
              </div>
            )}

            {setupStep === 2 && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-medium mb-2">
                    📱 Scansiona questo QR code con la tua app di autenticazione
                  </p>
                  <div className="bg-white p-4 rounded-lg flex justify-center">
                    {qrCode && <img src={qrCode} alt="QR Code" className="w-48 h-48" />}
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <Label className="text-xs text-slate-600 flex items-center gap-2 mb-2">
                    <Key className="h-4 w-4" />
                    Chiave Manuale (se non puoi scannerizzare)
                  </Label>
                  <code className="text-sm font-mono bg-white px-3 py-2 rounded border border-slate-300 block break-all">
                    {manualKey}
                  </code>
                </div>

                <div>
                  <Label htmlFor="verification-code">Codice di Verifica</Label>
                  <Input
                    id="verification-code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    className="mt-1 text-center text-2xl tracking-widest font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Inserisci il codice di 6 cifre dalla tua app
                  </p>
                </div>

                <Button
                  onClick={handleEnableSep}
                  disabled={verificationCode.length !== 6}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Attiva 2FA
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Disable Dialog */}
        <Dialog open={showDisable} onOpenChange={setShowDisable}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-6 w-6" />
                Disattiva 2FA
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  ⚠️ Disattivando la 2FA, il tuo account sarà meno sicuro. Assicurati di avere una password forte.
                </p>
              </div>

              <div>
                <Label htmlFor="disable-password">Password</Label>
                <Input
                  id="disable-password"
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Inserisci la tua password"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="disable-otp">Codice Attuale</Label>
                <Input
                  id="disable-otp"
                  type="text"
                  value={disableOtp}
                  onChange={(e) => setDisableOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength={6}
                  className="mt-1 text-center text-xl tracking-widest font-mono"
                />
              </div>

              <Button
                onClick={handleDisable2FA}
                disabled={!disablePassword || disableOtp.length !== 6}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                Disattiva 2FA
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TwoFactorSettings;
